import React, { useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpload, faDownload } from "@fortawesome/free-solid-svg-icons";
import "react-toastify/dist/ReactToastify.css";
import WhatsAppAPI from "../../api/WhatsAppAPI";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const ImportLeads = (props) => {
  const [file, setFile] = useState(null);
  const [isSpinner, setIsSpinner] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const csvData = [
    [
      "First Name",
      "Last Name",
      "Country Code",
      "WhatsApp Number",
      "Email",
      "DOB (DD-MM-YYYY)",
      "Address",
      "Tag Names (comma-separated)",
      "Description",
    ],
    [
      "John",
      "Doe",
      "91",
      "9876XXXXXX",
      "john@example.com",
      "28-05-1995",
      "Ajmer",
      "Hot lead, Warm lead",
      "Interested in product X",
    ],
  ];

  // Normalize header string: lowercase, remove parentheses, trim, collapse spaces
  const normalizeHeader = (header) =>
    header
      .toLowerCase()
      .replace(/\s+/g, " ")
      .replace(/[\(\)]/g, "")
      .trim();

  const convertToCSV = (array) =>
    array
      .map((row) =>
        row
          .map((value) =>
            typeof value === "string" && value.includes(",")
              ? `"${value}"`
              : value,
          )
          .join(","),
      )
      .join("\n");

  const downloadCSV = () => {
    const csvString = convertToCSV(csvData);
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "lead_records.csv");
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];

    if (selectedFile) {
      const validExtensions = ["xls", "xlsx", "csv"];
      const fileExtension = selectedFile.name.split(".").pop().toLowerCase();

      if (!validExtensions.includes(fileExtension)) {
        setErrorMessage("Only .csv, .xls, and .xlsx files are allowed.");
        setFile(null);
        e.target.value = "";
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const content = event.target.result;
        const rows = content
          .split("\n")
          .map((row) => row.trim())
          .filter((r) => r.length > 0);

        if (rows.length === 0) {
          setErrorMessage("The file is empty.");
          setFile(null);
          return;
        }

        // Normalize headers from the first row
        const headers = rows[0]
          .split(",")
          .map((header) => normalizeHeader(header));

        // Required headers normalized
        const requiredHeaders = [
          "first name",
          "last name",
          "country code",
          "whatsapp number",
          "email",
          "dob dd-mm-yyyy",
          "address",
          "tag names comma-separated",
          "description",
        ];

        const missingHeaders = requiredHeaders.filter(
          (h) => !headers.includes(h),
        );

        if (missingHeaders.length > 0) {
          setErrorMessage(
            `Invalid file format. Missing headers: ${missingHeaders.join(", ")}`,
          );
          setFile(null);
          e.target.value = "";
          return;
        }

        setFile(selectedFile);
        setErrorMessage("");
      };

      reader.readAsText(selectedFile);
    }
  };

  const readExcelFile = async (file) => {
    const reader = new FileReader();
    reader.onload = async (event) => {
      const data = new Uint8Array(event.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      if (!jsonData.length) {
        toast.error("The file is empty.");
        setIsSpinner(false);
        return;
      }

      const rawHeaders = jsonData[0];
      // Normalize all headers for comparison
      const formattedHeaders = rawHeaders.map((header) =>
        normalizeHeader(header || ""),
      );

      const headerMapping = {
        "first name": "firstname",
        "last name": "lastname",
        "country code": "country_code",
        "whatsapp number": "whatsapp_number",
        email: "email",
        "dob dd-mm-yyyy": "dob",
        address: "address",
        "tag names comma-separated": "tag_names",
        description: "description",
      };

      // Check for missing required headers
      const missingHeaders = Object.keys(headerMapping).filter(
        (h) => !formattedHeaders.includes(h),
      );
      if (missingHeaders.length > 0) {
        toast.error(`Missing required headers: ${missingHeaders.join(", ")}`);
        setIsSpinner(false);
        return;
      }

      // Map headers to their index in the row
      const headerIndexMap = {};
      formattedHeaders.forEach((header, index) => {
        if (headerMapping[header]) {
          headerIndexMap[headerMapping[header]] = index;
        }
      });

      // Parse rows to objects
      const finalJson = jsonData.slice(1).map((row) => {
        const rowData = {};
        Object.values(headerMapping).forEach((key) => {
          const idx = headerIndexMap[key];
          rowData[key] =
            idx !== undefined && row[idx] !== undefined ? row[idx] : "";
        });
        return rowData;
      });

      if (finalJson.length > 0) {
        try {
          const response = await WhatsAppAPI.importLeads({ leads: finalJson });
          if (response.success) {
            toast.success(
              `Imported ${response.createdLeads.length} leads. Skipped ${response.skippedLeads.length}.`,
            );
          } else {
            toast.error("Failed to import leads. Please check the data.");
          }
        } catch (error) {
          // console.error("Error importing leads:", error);
          toast.error("An error occurred during import.");
        } finally {
          setTimeout(() => {
            props.refreshData();
            setIsSpinner(false);
          }, 1000);
        }
      } else {
        toast.warn("No valid leads found in the file.");
        setIsSpinner(false);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const handleFileSubmit = () => {
    if (file) {
      setIsSpinner(true);
      readExcelFile(file);
    }
  };

  return (
    <div>
      <Modal
        show={props.show}
        onHide={props.onHide}
        centered
        size="md"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Import Leads</Modal.Title>
        </Modal.Header>

        {!isSpinner ? (
          <>
            <Modal.Body>
              <div className="p-3 rounded">
                <Form.Group controlId="formFile">
                  <Form.Label>
                    <strong>
                      <FontAwesomeIcon icon={faUpload} /> Upload File
                    </strong>
                  </Form.Label>
                  <Form.Control
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileChange}
                  />
                  {errorMessage && (
                    <Form.Text className="text-danger mt-1 d-block">
                      {errorMessage}
                    </Form.Text>
                  )}
                  <Form.Text className="text-muted">
                    <ul className="mt-3 ps-3 mb-0 small">
                      <li>
                        <strong>DOB</strong> should be <code>DD-MM-YYYY</code>
                      </li>
                      <li>
                        <strong>Tags</strong> must be comma-separated like{" "}
                        <code>Hot lead, Warm lead</code>
                      </li>
                      <li>
                        Headers required: <br />
                        <code>
                          First Name, Last Name, Country Code, WhatsApp Number,
                          Email, DOB (DD-MM-YYYY), Address, Tag Names
                          (comma-separated), Description
                        </code>
                      </li>
                    </ul>
                  </Form.Text>
                </Form.Group>
              </div>
            </Modal.Body>

            <Modal.Footer className="d-flex justify-content-between align-items-center">
              <Button variant="outline-primary" onClick={downloadCSV}>
                <FontAwesomeIcon icon={faDownload} className="me-2" />
                Download Dummy File
              </Button>

              <div>
                <Button variant="light" onClick={props.onHide} className="me-3">
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleFileSubmit}
                  disabled={!file}
                >
                  Submit
                </Button>
              </div>
            </Modal.Footer>
          </>
        ) : (
          <div className="p-5 d-flex justify-content-center align-items-center flex-column">
            <div className="sk-cube-grid mb-3">
              {[...Array(9)].map((_, i) => (
                <div key={i} className={`sk-cube sk-cube${i + 1}`}></div>
              ))}
            </div>
            <span>Importing leads, please wait...</span>
          </div>
        )}
      </Modal>
      <ToastContainer autoClose={30000} />
    </div>
  );
};

export default ImportLeads;
